import { IconButton } from '@volvo-cars/react-icons';
import { useEffect, useState } from 'react';

import { Tooltip, TooltipPlacement } from '@collab/atoms';

import useCopyDelayed from 'hooks/useCopyDelayed';

export type CopyButtonProps = {
  copy: string;
  tooltipPlacement: TooltipPlacement;
  disabled?: boolean;
  variant?: 'outlined' | 'filled' | 'clear';
  ariaLabel?: string;
  preCopy?: () => void;
};

const copiedText = 'Copied';
const failedToCopyText = 'Failed to copy';

const CopyButton: React.FC<CopyButtonProps> = ({
  copy,
  tooltipPlacement,
  variant = 'clear',
  disabled = false,
  ariaLabel,
  preCopy,
}) => {
  const { copySecret: onCopy, copied, copyError } = useCopyDelayed(copy);
  const [tooltipText, setTooltipText] = useState(copiedText);

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (copyError) {
      setTooltipText(failedToCopyText);
      timerId = setTimeout(() => {
        setTooltipText(copiedText);
      }, 3300);
    }

    return () => clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyError]);

  const isDisabled = copied || disabled;

  return (
    <Tooltip
      text={tooltipText}
      visible={copied || copyError}
      placement={tooltipPlacement}
    >
      <IconButton
        icon="stacked-squares"
        onClick={() => {
          if (preCopy) {
            preCopy();
          }
          onCopy();
        }}
        disabled={isDisabled}
        aria-label={ariaLabel ?? 'Copy text'}
        variant={variant}
      />
    </Tooltip>
  );
};

export default CopyButton;
